import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  ExportIcon,
  PdfIcon,
  EditIcon,
  TrashIcon,
  CancelIcon,
  ArchiveIcon,
  UnarchiveIcon,
  MergeIcon,
} from '@/components/ui-bridge/icons/ActionIcons';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import ReportOptions from '@/components/ReportOptions/ReportOptions';

function OrderSelectionMultiple({
  actionHandler,
  currentPage,
  flags,
  itemsAmount,
  multipleArchiveAllowed,
  orderId,
  orderStatus,
  orderType,
  isArchived,
}) {
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);

  const supportMode = document.cookie.replace(/(?:(?:^|.*;\s*)SupportMode\s*=\s*([^;]*).*$)|^.*$/, '$1') === 'true';

  const orderTypeConfigurations = useMemo(() => {
    const configurationString = window.sessionStorage.getItem('orderTypeConfigurations');
    return configurationString ? JSON.parse(configurationString) : null;
  }, []);

  const showReportOptionsHandler = event => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const closeReportOptionsHandler = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  const deleteHandler = event => {
    const message = intl.formatMessage({ id: 'MESSAGE.DELETE_CONFIRMATION' });
    if (window.confirm(message)) {
      actionHandler(event, 'delete');
    }
  };

  const cancelHandler = event => {
    actionHandler(event, 'cancel');
  };

  let parentOrderType = orderType;
  if (orderType?.startsWith('marketplace_')) {
    parentOrderType = parentOrderType.replace('marketplace_', '');
  }

  let configurations = {
    canBeArchived: multipleArchiveAllowed,
  };

  if (
    orderTypeConfigurations &&
    orderTypeConfigurations[parentOrderType] &&
    orderTypeConfigurations[parentOrderType][orderStatus]
  ) {
    configurations = orderTypeConfigurations[parentOrderType][orderStatus];
  }

  const getButtonClassName = disabled =>
    clsx(
      'inline-flex h-[2vw] min-h-[32px] w-[10vw] min-w-[150px] items-center justify-start border bg-white px-[6px] py-[2px]',
      'text-[calc(6px+0.4vw)] font-["Open_Sans",sans-serif] normal-case transition-colors',
      disabled
        ? 'cursor-default text-black/26 border-black/12 pointer-events-none'
        : 'cursor-pointer border-[#dedede] text-black hover:border-[#0062cc]'
    );
  const iconClassName = 'mr-[3px] flex-shrink-0 text-current';
  const iconStyle = { fontSize: '1.2vw', width: '1em', height: '1em', minWidth: '48px', minHeight: '24px' };

  return (
    <div className="h-full text-center">
      <div className="mx-[1vw] min-h-[calc(100%-4px)] border-2 border-solid border-[#dedede] bg-[#eaeaea]">
        <div className="mt-[6vh] text-[calc(16px+0.4vw)]">
          <span>
            <FormattedMessage id="items" values={{ items: itemsAmount }} />
          </span>
        </div>
        <div className="text-[calc(14px+0.4vw)]">
          <span>
            <FormattedMessage id="selected" />
          </span>
        </div>

        <div className="mb-[5vh] mt-[30px]">
          <div className="mt-[2vh]">
            <button
              type="button"
              disabled={configurations.canBeExported !== true}
              className={getButtonClassName(configurations.canBeExported !== true)}
              onClick={event => actionHandler(event, 'export')}
            >
              <ExportIcon className={iconClassName} style={iconStyle} />
              <FormattedMessage id="export" />
            </button>
          </div>

          <div className="mt-[2vh]">
            <button
              type="button"
              disabled={configurations.canGenerateReport !== true}
              className={getButtonClassName(configurations.canGenerateReport !== true)}
              onClick={showReportOptionsHandler}
            >
              <PdfIcon className={iconClassName} style={iconStyle} />
              <FormattedMessage id="generatePdf" />
            </button>
          </div>

          <div className="mt-[2vh]">
            <button
              type="button"
              disabled={configurations.canBeEdited !== true}
              className={getButtonClassName(configurations.canBeEdited !== true)}
              onClick={event => actionHandler(event, 'edit')}
            >
              <EditIcon className={iconClassName} style={iconStyle} />
              <FormattedMessage id="edit" />
            </button>
          </div>

          <div className="mt-[2vh]">
            {currentPage === 'independent' ? (
              <button
                type="button"
                disabled={configurations.canBeDeleted !== true}
                className={getButtonClassName(configurations.canBeDeleted !== true)}
                onClick={deleteHandler}
              >
                <TrashIcon className={iconClassName} style={iconStyle} />
                <FormattedMessage id="delete" />
              </button>
            ) : (
              <button
                type="button"
                disabled={configurations.canBeCanceled !== true}
                className={getButtonClassName(configurations.canBeCanceled !== true)}
                onClick={cancelHandler}
              >
                <CancelIcon className={iconClassName} style={iconStyle} />
                <FormattedMessage id="cancel" />
              </button>
            )}
          </div>

          <div className="mt-[2vh]">
            {isArchived ? (
              <button
                type="button"
                disabled={configurations.canBeArchived !== true}
                className={getButtonClassName(configurations.canBeArchived !== true)}
                onClick={event => actionHandler(event, 'unarchive')}
              >
                <UnarchiveIcon className={iconClassName} style={iconStyle} />
                <FormattedMessage id="unarchive" />
              </button>
            ) : (
              <button
                type="button"
                disabled={configurations.canBeArchived !== true}
                className={getButtonClassName(configurations.canBeArchived !== true)}
                onClick={event => actionHandler(event, 'archive')}
              >
                <ArchiveIcon className={iconClassName} style={iconStyle} />
                <FormattedMessage id="archive" />
              </button>
            )}
          </div>

          {currentPage === 'independent' && supportMode ? (
            <div className="mt-[2vh]">
              <button
                type="button"
                className={getButtonClassName(false)}
                onClick={event => actionHandler(event, 'merge-selection')}
              >
                <MergeIcon className={iconClassName} style={iconStyle} />
                <FormattedMessage id="merge" />
              </button>
            </div>
          ) : null}
        </div>

        <ReportOptions
          show={open}
          closeHandler={closeReportOptionsHandler}
          anchorEl={anchorEl}
          options={configurations.reportsOptions}
          entityId={orderId}
          flags={flags}
        />
      </div>
    </div>
  );
}

OrderSelectionMultiple.propTypes = {
  itemsAmount: PropTypes.number.isRequired,
  orderType: PropTypes.string,
  orderStatus: PropTypes.string,
  orderId: PropTypes.string,
  actionHandler: PropTypes.func.isRequired,
  currentPage: PropTypes.string.isRequired,
  flags: PropTypes.object,
  multipleArchiveAllowed: PropTypes.bool.isRequired,
  isArchived: PropTypes.bool.isRequired,
};

OrderSelectionMultiple.defaultProps = {
  orderId: null,
  orderStatus: null,
  orderType: null,
  flags: {},
};

export default OrderSelectionMultiple;
